import moment from "moment";

// aiim.datasets
import FieldNames from "../../../../../../aiim/datasets/FieldNames";

// aiim.util
import { findField, getAttributeValue } from "../../../../../../aiim/util/aiimUtil";

// components.main.InfoPanel
import { generateShareUrl } from "../../../../InfoPanel/shareUtil";

// context
import Context from "../../../../../../context/Context";
import Topic from "../../../../../../context/Topic";

// main.More.Actions.BookWorkspace.WorkspaceReservation
import BookingSystem from "./BookingSystem";

const PENDING_APPROVAL = 0;
const APPROVED = 1;
const CANCELED = 3;
const CHECKED_IN = 4;
const CHECKED_OUT = 5;

const ALL_DAY = 1;
const NOT_ALL_DAY = 0;

export default class EsriReservationSchema extends BookingSystem {
  static instance;

  constructor() {
    super();
    this.type = "esri";
  }

  static getInstance() {
    if (!this.instance) {
      this.instance = new EsriReservationSchema();
    }
    return this.instance;
  }

  login = () => {
    if (Context.getInstance().user.isAnonymous()) {
      Topic.publish(Topic.SignInClicked, {});
    }
    return Promise.resolve();
  };

  getAccessToken = () => {
    const esriId = Context.instance.lib.esri.esriId;
    const credential = esriId.findCredential(Context.instance.getPortalUrl());
    return Promise.resolve(credential && credential.token);
  };

  getBookingSchedule = (accessToken, params) => {
    const { checkInDate, checkOutDate, unitId } = params;
    return this.checkAvailability([unitId], checkInDate, checkOutDate);
  };

  logout = () => {
    return;
  };

  getLoggedInUser = () => {
    return Context.getInstance().user.portalUser;
  };

  bookWorkspace = (accessToken, params) => {
    const reservations = Context.instance.aiim.datasets.reservations;
    const layer = reservations && reservations.layer2D;
    if (!layer) {
      return Promise.reject("No RESERVATIONS layer found");
    }

    return new Promise((resolve, reject) => {
      const reservation = this._createReservationFeature(params);

      this._addReservationFeature(reservation, layer)
        .then((results) => {
          const addFeatureResults = results.addFeatureResults;
          const featureResult = (
            addFeatureResults &&
            addFeatureResults.length === 1 &&
            addFeatureResults[0]
          );
          const objectId = featureResult && featureResult.objectId;
          if (objectId) {
            params.objectId = objectId;
          } else {
            console.error(featureResult);
            throw new Error("Unable to add reservation");
          }

          return this._verifyUnitAvailability(params, reservation, layer);
        })
        .then(() => {
          resolve({ reservation, params });
          Topic.publish(Topic.ReservationsUpdated,{});
        }).catch((e) => {
          reject(e);
        });
    });
  };

  _addReservationFeature = (reservation, layer) => {
    const options = {
      rollbackOnFailureEnabled: true
    };
    return layer.applyEdits({ addFeatures: [reservation] }, options);
  }

  _verifyUnitAvailability = (params, reservation, layer) => {
    return new Promise((resolve, reject) => {
      const { objectId } = params;

      const attrs = reservation.attributes;
      const startTime = getAttributeValue(attrs, FieldNames.START_TIME);
      const endTime = getAttributeValue(attrs, FieldNames.END_TIME);
      const unitId = getAttributeValue(attrs, FieldNames.UNIT_ID);

      this.checkAvailability([unitId], startTime, endTime, objectId)
        .then((availableUnits) => {
          if (availableUnits && availableUnits.length === 1 && availableUnits[0] === unitId) {
            return this._updateReservationFeature(objectId, layer);
          } else {
            throw new Error("Booking conflict, deleting reservation...");
          }
        })
        .then(() => {
          resolve();
        })
        .catch((e) => {
          this._deleteConflictingReservation(objectId, layer);
          reject(e);
        });
    });
  }

  _updateReservationFeature = (objectId, layer) => {
    if (!objectId) {
      return Promise.resolve();
    }

    const reservations = Context.getInstance().aiim.datasets.reservations;
    const objectIdField = layer.objectIdField;
    const update = { attributes: {} };
    update.attributes[objectIdField] = objectId;
    update.attributes[reservations.stateField] = APPROVED;
    const options = {
      rollbackOnFailureEnabled: true
    };
    return layer.applyEdits({ updateFeatures: [update] }, options);
  }

  _deleteConflictingReservation = (objectId, layer) => {
    if (!objectId) {
      return Promise.resolve();
    }

    const options = {
      rollbackOnFailureEnabled: true
    };
    return layer.applyEdits({ deleteFeatures: [{ objectId }] }, options);
  }

  _createReservationFeature = (params) => {
    const { unit, checkIn, checkOut, allDay, item } = params;
    const reservations = Context.instance.aiim.datasets.reservations;
    const unitsDataset = Context.instance.aiim.datasets.units;
    const i18n = Context.instance.i18n;
    const user = this.getLoggedInUser();
    const state = PENDING_APPROVAL;
    const allDayValue = allDay ? ALL_DAY : NOT_ALL_DAY;

    const attributes = {};
    const geometry = unit.geometry;

    // RESERVED_BY_USERNAME, RESERVED_FOR_USERNAME
    // attributes[reservations.reservedByUsernameField] = user.username;
    attributes[reservations.reservedForUsernameField] = user.username;

    // RESERVED_FOR_FULL_NAME
    attributes[reservations.reservedForFullNameField] = user.fullName;

    // STATE
    attributes[reservations.stateField] = state;

    // START_TIME, END_TIME
    attributes[reservations.startTimeField] = new Date(checkIn).getTime();
    attributes[reservations.endTimeField] = new Date(checkOut).getTime();

    // ALL_DAY
    attributes[reservations.allDayField] = allDayValue;

    // UNIT_ID, UNIT_NAME, LEVEL_ID
    const unitId = getAttributeValue(unit.attributes, FieldNames.UNIT_ID);
    const unitName = getAttributeValue(unit.attributes, FieldNames.NAME);
    const levelData = unitsDataset.getLevelData(unit);
    const levelId = levelData && levelData.levelId;
    if(reservations.unitIdField) attributes[reservations.unitIdField] = unitId;
    if(reservations.unitNameField) attributes[reservations.unitNameField] = unitName;
    if(reservations.levelIdField) attributes[reservations.levelIdField] = levelId;

    // TITLE, DESCRIPTION (optional layer fields)
    const titleField = reservations.titleField;
    const descriptionField = reservations.descriptionField;

    if (titleField && params.title) {
      attributes[titleField] = params.title;
    } else {
      // Populate title with "Booked Workspace - {UNIT_NAME}"
      if (titleField && unitName) {
        const title = i18n.more.bookWorkspace.emailSubject.replace("{workspaceName}", unitName);
        attributes[titleField] = title;
      }
    }

    if (descriptionField && params.description) {
      attributes[descriptionField] = params.description;
    } else if (!params.noAutoDescription){
      // Populate description with hyperlink to unit feature
      if (descriptionField && item) {
        const locationUrl = generateShareUrl(item);
        const description = locationUrl;
        attributes[descriptionField] = description;
      }
    }

    return { attributes, geometry };
  }

  checkAvailability = (unitIds, checkIn, checkOut, ignoreObjectId) => {
    return new Promise((resolve, reject) => {
      const reservations = Context.instance.aiim.datasets.reservations;
      // if(!reservations) return;
      const layer = reservations && reservations.layer2D;

      const ids = unitIds.map((unitId) => `'${unitId}'`);
      const whereClauses = [
        `${reservations.unitIdField} IN (${ids.join(", ")})`
      ];
      // Ignore the specified objectId
      if (ignoreObjectId && layer) {
        whereClauses.push(`${layer.objectIdField} <> ${ignoreObjectId}`);
      }
      const where = `${whereClauses.join(" AND ")}`;

      const availableUnits = [];
      reservations.query(where)
        .then((data) => {
          const features = data.features;
          const reservedUnits = {};
          features.forEach((reservation) => {
            const unitId = getAttributeValue(reservation.attributes, FieldNames.UNIT_ID);
            if (!reservedUnits[unitId]) {
              reservedUnits[unitId] = [reservation];
            } else {
              reservedUnits[unitId].push(reservation);
            }
          });

          unitIds.forEach((unitId) => {
            if (reservedUnits[unitId]) {
              const unitReservations = reservedUnits[unitId];
              if (this._canReserve(unitReservations, checkIn, checkOut)) {
                availableUnits.push(unitId);
              }
            } else {
              availableUnits.push(unitId);
            }
          });

          resolve(availableUnits);
        })
        .catch((e) => {
          reject(e);
        });
    });
  };

  _canReserve(unitReservations, checkIn, checkOut) {
    let canReserve = true;
    unitReservations.forEach((unitReservation) => {
      const attrs = unitReservation.attributes;
      const reservationState = getAttributeValue(attrs, FieldNames.STATE);
      const reservationStart = getAttributeValue(attrs, FieldNames.START_TIME);
      const reservationEnd = getAttributeValue(attrs, FieldNames.END_TIME);

      const mReservationStart = moment(reservationStart);
      const mReservationEnd = moment(reservationEnd);
      const mCheckIn = moment(checkIn);
      const mCheckOut = moment(checkOut);

      const activeReservation =
        reservationState === PENDING_APPROVAL ||
        reservationState === APPROVED ||
        reservationState === CHECKED_IN;
        const notAvailable =
        (mReservationStart < mCheckIn && mReservationEnd > mCheckIn) ||
        (mReservationStart >= mCheckIn && mCheckOut > mReservationStart);

      if (activeReservation && notAvailable) {
        canReserve = false;
      }
    });

    return canReserve;
  }

  cancelBooking =(attributes)=> {
    const reservations = Context.instance.aiim.datasets.reservations;
    const layer = reservations && reservations.layer2D;
    if (!layer) {
      return Promise.resolve();
    }

    const objectIdField = layer.objectIdField;
    const objectId = attributes[objectIdField];

    const update = { attributes: {} };
    update.attributes[objectIdField] = objectId;
    update.attributes[reservations.stateField] = CANCELED;
    const options = {
      rollbackOnFailureEnabled: true
    };
    return layer.applyEdits({ updateFeatures: [update] }, options);
  }

  queryCheckInOut =(uniqueId)=> {
    const reservations = Context.instance.aiim.datasets.reservations;
    const layer = reservations && reservations.layer2D;
    let objectId;
    if(layer && layer.objectIdField) objectId = layer.objectIdField;
    let url = layer.url;
    let layerId = layer.layerId;
    url = url + "/" + layerId
    url = Context.checkMixedContent(url);
    const lib = Context.getInstance().lib;
    const task = new lib.esri.QueryTask({url: url});
    const query = new lib.esri.Query();
    query.outFields = ["*"];
    query.returnGeometry = true;
    query.returnZ = true;
    query.where = objectId + " = " + uniqueId;
    return task.execute(query);
  }


  updateCheckInOut =(attributes, stateVal)=> {
    const reservations = Context.instance.aiim.datasets.reservations;
    const layer = reservations && reservations.layer2D;
    const fields = layer && layer.fields;
    let objectId;
    if(layer && layer.objectIdField) objectId = layer.objectIdField;
    const checkInTime = findField(fields, FieldNames.CHECK_IN_TIME).name;
    const checkOutTime = findField(fields, FieldNames.CHECK_OUT_TIME).name;
    const state = findField(fields, FieldNames.STATE).name;

    const checkInTimeVal = getAttributeValue(attributes, FieldNames.CHECK_IN_TIME);
    const objectIdVal = getAttributeValue(attributes,objectId);
    const checkOutTimeVal = getAttributeValue(attributes, FieldNames.CHECK_OUT_TIME);

    const update = { attributes: {} };
    update.attributes[objectId] = objectIdVal;
    update.attributes[checkInTime] = checkInTimeVal;
    update.attributes[checkOutTime] = checkOutTimeVal;
    if(stateVal === "checkIn") update.attributes[state] = CHECKED_IN;
    else if(stateVal === "checkOut") update.attributes[state] = CHECKED_OUT;
    const options = {
      rollbackOnFailureEnabled: true
    };
    return layer.applyEdits({ updateFeatures: [update] }, options);
  }

}
